import Table from "@shared/Table";
import {SpinnerWrapper} from "@shared/Spinner";
import useAllStallholders from "@hooks/useAllStallholders";
import useAdminAPIService from "@hooks/useAdminAPIService";
import { useState, useEffect, Fragment } from 'react';
import { Stallholder } from "@sharedtypes/Stallholder";
import { Field, Switch } from '@headlessui/react'
import classnames from 'classnames';
import log from "loglevel";
import { Transition, TransitionChild, Dialog, DialogPanel } from '@headlessui/react'
import { useQueryClient } from '@tanstack/react-query';

const RENT_METHOD_LABELS: Record<number, string> = {
  0: "Standing Order",
  1: "From Takings",
  2: "Invoice",
};

export function DealerManagement() {

  const { isLoading, data: stallholders } = useAllStallholders();
  const [showAddDealerDlg, setShowAddDealerDlg] = useState(false);
  const [editStallholder, setEditStallholder] = useState<Stallholder | undefined>();
  const [showEditDlg, setShowEditDlg] = useState(false);

  function openEdit(stallholder: Stallholder) {
    setEditStallholder(stallholder);
    setShowEditDlg(true);
  }

  const columns = [
    {
      "id": "dealer_id",
      "label": "ID",
      "cellStyle": (row: number) => "px-6 py-1 font-medium text-lg text-gray-900 whitespace-nowrap bg-gray-50 dark:text-white dark:bg-gray-800",
      "headerStyle": "px-6 py-3 bg-gray-50 dark:bg-gray-800",
    },
    {
      "id": "name",
      "label": "Name",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4"
    },
    {
      "id": "rent",
      "label": "Rent",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4",
      "format": (cell: any) => cell ? `£${cell}` : "-"
    },
    {
      "id": "rentPaymentMethod",
      "label": "Payment Method",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4",
      "format": (cell: any) => cell !== undefined && cell !== null ? RENT_METHOD_LABELS[cell] || "-" : "-"
    },
    {
      "id": undefined,
      "label": "Active",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4",
      "format": (cell: any, row: {}) => {
        const s = row as Stallholder;
        return <HideToggle stallholder={s} />;
      }
    },
    {
      "id": undefined,
      "label": "",
      "cellStyle": (row: number) => "px-6 py-1 text-lg",
      "headerStyle": "px-6 py-4",
      "format": (cell: any, row: {}) => {
        const s = row as Stallholder;
        return (
          <button
            type="button"
            onClick={() => openEdit(s)}
            className="rounded-md bg-indigo-50 px-2.5 py-1.5 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-100"
          >
            Manage
          </button>
        );
      }
    },
  ];

  return (
    <div>
      <AddDealerDlg show={showAddDealerDlg} hide={() => setShowAddDealerDlg(false)} />
      {editStallholder && (
        <EditDealerDlg show={showEditDlg} hide={() => setShowEditDlg(false)} stallholder={editStallholder} />
      )}

      {
        isLoading || !stallholders ?
        <SpinnerWrapper /> :
        <div>
          <div className="flex justify-between items-center mt-4 mb-6">
            <h1 className="text-2xl">{stallholders.length} Dealers</h1>
            <button
              type="button"
              onClick={() => setShowAddDealerDlg(true)}
              className="rounded-md bg-emerald-500 px-2.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600"
            >
              Add Dealer
            </button>
          </div>
          <Table data={stallholders} columns={columns} />
        </div>
      }
    </div>
  );
}

type HideToggleProps = {
  stallholder: Stallholder;
};

const HideToggle = ({ stallholder }: HideToggleProps) => {

  const [active, setActive] = useState(!stallholder.hide);
  const [updating, setUpdating] = useState(false);
  const apiService = useAdminAPIService();
  const queryClient = useQueryClient();

  async function toggle(newActive: boolean) {
    setUpdating(true);
    setActive(newActive);

    await apiService.fetch("updateStallholder", { id: stallholder.id, hide: !newActive })
      .then(() => {
        log.info("Updated hide for " + stallholder.dealer_id);
        queryClient.invalidateQueries({ queryKey: ['allStallholders'] });
        queryClient.invalidateQueries({ queryKey: ['stallholders'] });
      })
      .catch((error) => {
        log.error("Error updating hide:", error);
        setActive(!newActive);
      });

    setUpdating(false);
  }

  return (
    <Field as="div" className="">
      <Switch
        checked={active}
        onChange={toggle}
        disabled={updating}
        className={classnames(
          active ? 'bg-indigo-600' : 'bg-gray-200',
          'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 disabled:opacity-50'
        )}
      >
        <span
          aria-hidden="true"
          className={classnames(
            active ? 'translate-x-5' : 'translate-x-0',
            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
          )}
        />
      </Switch>
    </Field>
  );
}

type EditDealerDlgProps = {
  show: boolean;
  hide: () => void;
  stallholder: Stallholder;
};

const EditDealerDlg = ({ show, hide, stallholder }: EditDealerDlgProps) => {

  const [rent, setRent] = useState(stallholder.rent?.toString() || "");
  const [rentPaymentMethod, setRentPaymentMethod] = useState(
    stallholder.rentPaymentMethod !== undefined && stallholder.rentPaymentMethod !== null
      ? stallholder.rentPaymentMethod.toString()
      : ""
  );
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiService = useAdminAPIService();
  const queryClient = useQueryClient();

  useEffect(() => {
    setRent(stallholder.rent?.toString() || "");
    setRentPaymentMethod(
      stallholder.rentPaymentMethod !== undefined && stallholder.rentPaymentMethod !== null
        ? stallholder.rentPaymentMethod.toString()
        : ""
    );
    setError(null);
  }, [stallholder]);

  function close() {
    setError(null);
    hide();
  }

  async function save() {
    setProcessing(true);
    setError(null);

    const payload: any = { id: stallholder.id };
    if (rent !== "") payload.rent = Number(rent);
    if (rentPaymentMethod !== "") payload.rentPaymentMethod = Number(rentPaymentMethod);

    await apiService.fetch("updateStallholder", payload)
      .then(() => {
        log.info("Updated stallholder " + stallholder.dealer_id);
        queryClient.invalidateQueries({ queryKey: ['allStallholders'] });
        queryClient.invalidateQueries({ queryKey: ['stallholders'] });
        close();
      })
      .catch((err: any) => {
        log.error("Error updating stallholder:", err);
        setError(err?.message || "Failed to update");
      });

    setProcessing(false);
  }

  return (
    <div className="">
      <Transition appear show={show} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => {}}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">

                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Manage {stallholder.name} ({stallholder.dealer_id})
                  </h2>

                  {error && (
                    <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
                  )}

                  <div className="mb-3">
                    <label htmlFor="edit-rent" className="block text-sm font-medium text-gray-700 mb-1">Monthly Rent (£)</label>
                    <input
                      id="edit-rent"
                      type="number"
                      min="0"
                      step="0.01"
                      value={rent}
                      onChange={(e) => setRent(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-300 sm:text-sm sm:leading-6"
                    />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="edit-rent-method" className="block text-sm font-medium text-gray-700 mb-1">Rent Payment Method</label>
                    <select
                      id="edit-rent-method"
                      value={rentPaymentMethod}
                      onChange={(e) => setRentPaymentMethod(e.target.value)}
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-slate-300 sm:text-sm sm:leading-6"
                    >
                      <option value="">-- Select --</option>
                      <option value="0">Standing Order</option>
                      <option value="1">From Takings</option>
                      <option value="2">Invoice</option>
                    </select>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={close}
                      disabled={processing}
                      className="rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={save}
                      disabled={processing}
                      className="rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 disabled:bg-gray-300 disabled:text-gray-500"
                    >
                      {processing ? "Saving..." : "Save"}
                    </button>
                  </div>

                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

type AddDealerDlgProps = {
  show: boolean;
  hide: () => void;
};

const AddDealerDlg = ({ show, hide }: AddDealerDlgProps) => {

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dealerId, setDealerId] = useState("");
  const [rentPaymentMethod, setRentPaymentMethod] = useState("");
  const [rent, setRent] = useState("");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ sub: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const apiService = useAdminAPIService();

  function reset() {
    setName("");
    setEmail("");
    setDealerId("");
    setRentPaymentMethod("");
    setRent("");
    setResult(null);
    setError(null);
    setProcessing(false);
  }

  function close() {
    reset();
    hide();
    if (result) window.location.reload();
  }

  async function submit() {
    setProcessing(true);
    setError(null);

    const payload: any = { name: name, email: email, dealer_id: dealerId };
    if (rentPaymentMethod !== "") payload.rentPaymentMethod = Number(rentPaymentMethod);
    if (rent !== "") payload.rent = Number(rent);

    await apiService.fetch("createUser", payload)
      .then((data: any) => {
        log.info("User created " + JSON.stringify(data));
        setResult(data);
      })
      .catch(function (err: any) {
        log.error("Error creating user:" + err);
        setError(err?.message || "Failed to create user");
      });

    setProcessing(false);
  }

  const canSubmit = name.length > 0 && email.length > 0 && dealerId.length > 0 && rentPaymentMethod !== "" && rent.length > 0 && !processing;

  return (
    <div className="">
      <Transition appear show={show} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => {}}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25" />
          </TransitionChild>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">

                  {!result ? (
                    <>
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Add New Dealer</h2>

                      {error && (
                        <div className="mb-4 rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
                      )}

                      <div className="mb-3">
                        <label htmlFor="add-name" className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                          id="add-name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-300 sm:text-sm sm:leading-6"
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="add-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                        <input
                          id="add-email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-300 sm:text-sm sm:leading-6"
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="add-dealer-id" className="block text-sm font-medium text-gray-700 mb-1">Dealer ID</label>
                        <input
                          id="add-dealer-id"
                          value={dealerId}
                          onChange={(e) => setDealerId(e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-300 sm:text-sm sm:leading-6"
                        />
                      </div>

                      <div className="mb-3">
                        <label htmlFor="add-rent" className="block text-sm font-medium text-gray-700 mb-1">Monthly Rent (£)</label>
                        <input
                          id="add-rent"
                          type="number"
                          min="0"
                          step="0.01"
                          value={rent}
                          onChange={(e) => setRent(e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-slate-300 sm:text-sm sm:leading-6"
                        />
                      </div>

                      <div className="mb-4">
                        <label htmlFor="add-rent-method" className="block text-sm font-medium text-gray-700 mb-1">Rent Payment Method</label>
                        <select
                          id="add-rent-method"
                          value={rentPaymentMethod}
                          onChange={(e) => setRentPaymentMethod(e.target.value)}
                          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-slate-300 sm:text-sm sm:leading-6"
                        >
                          <option value="">-- Select --</option>
                          <option value="0">Standing Order</option>
                          <option value="1">From Takings</option>
                          <option value="2">Invoice</option>
                        </select>
                      </div>

                      <div className="flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={close}
                          disabled={processing}
                          className="rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={submit}
                          disabled={!canSubmit}
                          className="rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600 disabled:bg-gray-300 disabled:text-gray-500"
                        >
                          {processing ? "Creating..." : "Create"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h2 className="text-lg font-semibold text-gray-900 mb-4">Dealer Created</h2>
                      <p className="text-sm text-gray-600 mb-3">
                        The dealer has been created. An email with their login details and temporary password has been sent to their email address. They will be asked to change their password on first login.
                      </p>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={close}
                          className="rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-600"
                        >
                          Done
                        </button>
                      </div>
                    </>
                  )}

                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default DealerManagement;
